import { saveFileToFolder } from "./saveFolder";
// Invoice workbook dispatcher.
//
// Routing by broker name:
//   Malca-Amit (any direction)          → Malca Courier Worksheet
//   LALI / EFFY / Iberjoya / MWI        → Orion direct activity log
//   DAI                                 → no workbook (flat rate; forward
//                                         the assignment email per FAQ)
//   Anything else                       → null + reason
//
// Multi-guard jobs (numGuards > 1):
//   - Generates ONE workbook per assigned guard.
//   - All guards get the same hours, locations, and HAWBs.
//   - Own-car guards get the mileage filled in (default: G1/G3 drive).
//   - Passengers (ride along) get blank mileage cells.
//   - Each filename is suffixed with -G{N}-{LastName}.
//
// Per Orion FAQ: each invoice goes to accountspayable@orionsecurityservices.com,
// one job per email per company.

import { generateMalcaWorkbook } from "./malcaExcel";
import { generateOrionDirectWorkbook } from "./orionDirectExcel";
import { guardOwnsCar } from "./billing";

function brokerKey(name) {
  return String(name || "").toLowerCase().replace(/[^a-z]/g, "");
}

const ORION_DIRECT_BROKERS = new Set(["lali", "effy", "iberjoya", "mwi"]);

/** Pick which workbook (if any) applies to this job + broker. */
export function chooseInvoiceForm(job, broker) {
  const key = brokerKey(broker?.name || job?.brokerName);

  // Brokers configured for "Bundled by sales rep" billing (e.g. DAI) submit
  // by forwarding the schedule email — no per-job worksheet. The Invoices
  // page handles bundling and the email composition separately.
  if (broker?.billingType === "bundled" || key === "dai") {
    return {
      kind: "none",
      reason: `${broker?.name || "This broker"} is bundled / flat rate — forward the assignment email to accountspayable@orionsecurityservices.com instead of generating a worksheet.`,
    };
  }

  if (key.includes("malca")) return { kind: "malca" };
  // Substring match (not exact) so full broker names resolve — e.g.
  // "LALI Jewels" → key "lalijewels" still maps to the LALI direct form.
  if ([...ORION_DIRECT_BROKERS].some((b) => key.includes(b))) return { kind: "orion-direct" };
  return {
    kind: "none",
    reason: `No invoice form mapped for broker "${broker?.name || job?.brokerName || "(unknown)"}". Add it to invoiceWorkbook.js.`,
  };
}

// Build the per-guard list to invoice. Falls back to a single anonymous guard
// for legacy single-guard jobs that don't have guardAssignments populated.
function guardsToInvoice(job) {
  const numGuards = Math.max(1, Number(job?.numGuards) || 1);
  const assignments = Array.isArray(job?.guardAssignments) ? job.guardAssignments : [];
  const out = [];
  for (let n = 1; n <= numGuards; n++) {
    const a = assignments.find((g) => g.guardNumber === n);
    const isDriver = guardOwnsCar(a, n);
    out.push({
      guardNumber: n,
      guardName: a?.guardName || "",
      // Own-car guards bill mileage; passengers get blank mileage cells.
      isDriver,
      // Per-leg miles this car drove (from the per-stop coverage in the
      // review). When present, the worksheet fills these exact legs instead
      // of the full route's segments.
      billedSegments: Array.isArray(a?.billedSegments) ? a.billedSegments : null,
    });
  }
  return out;
}

// Direction → 2-letter code used in filenames.
const DIRECTION_CODE = { outbound: "OB", inbound: "IB", direct: "DR", roundtrip: "RT" };

function tsToDate(ts) {
  if (!ts) return null;
  let ms = null;
  if (typeof ts === "number") ms = ts;
  else if (typeof ts === "string") ms = Date.parse(ts);
  else if (ts.toMillis) ms = ts.toMillis();
  else if (ts.seconds != null) ms = ts.seconds * 1000;
  if (ms == null || Number.isNaN(ms)) return null;
  return new Date(ms);
}

// The canonical job date (scheduled date preferred), parsed at local noon
// so a YYYY-MM-DD string can't shift a day across a timezone boundary.
function jobDate(job) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(job.scheduledDate || "");
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  return tsToDate(job.completedAt || job.completedAtMs || job.startedAt) || new Date();
}

// Build the workbook filename.
//   Directs:  {BROKER}_{M_D_YYYY}_{JOBTYPE}      e.g. MWI_6_7_2026_DIRECT
//             (a _G{N} suffix is added only when a job has multiple guards,
//              so per-guard files don't overwrite each other).
//   Else:     {BROKER}_{M_D_YYYY}_{DIR}_JOB_{JOBNUM}_GRD{N}  (Malca OB/IB)
function buildFilename(job, brokerLabel, guardNumber, totalGuards = 1) {
  const d = jobDate(job);
  const date = `${d.getMonth() + 1}_${d.getDate()}_${d.getFullYear()}`;
  const broker = brokerLabel.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const type = String(job.jobType || job.type || "").toLowerCase();
  if (type === "direct") {
    const suffix = totalGuards > 1 ? `_G${guardNumber}` : "";
    // Orion's own directs (MWI, LALI, EFFY, Iberjoya) carry no broker job
    // number, so the date is the only identifier. A Malca direct DOES have
    // one — leaving it out made the file unfindable by job number, and two
    // directs on the same date would overwrite each other.
    const num = String(job.jobNumber || "").replace(/[^A-Za-z0-9]+/g, "_");
    return num
      ? `${broker}_${date}_DIRECT_JOB_${num}${suffix}.xlsx`
      : `${broker}_${date}_DIRECT${suffix}.xlsx`;
  }
  const dir = DIRECTION_CODE[type] || "JOB";
  const num = String(job.jobNumber || job.id || "JOB").replace(/[^A-Za-z0-9]+/g, "_");
  return `${broker}_${date}_${dir}_JOB_${num}_GRD${guardNumber}.xlsx`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Generate the per-guard workbook Blobs for this job.
 * @returns {Promise<{
 *   files: { blob: Blob, filename: string, guardNumber: number, guardName: string }[],
 *   kind: "malca"|"orion-direct"|"none",
 *   reason?: string,
 * }>}
 */
export async function generateInvoiceWorkbooks(job, broker) {
  const choice = chooseInvoiceForm(job, broker);
  if (choice.kind === "none") return { files: [], kind: "none", reason: choice.reason };

  const guards = guardsToInvoice(job);
  const generate = choice.kind === "malca" ? generateMalcaWorkbook : generateOrionDirectWorkbook;
  // Broker label drives the filename prefix. Malca always renders as "MALCA".
  // Orion direct uses the broker name (EFFY, LALI, IBERJOYA, MWI).
  const brokerLabel = choice.kind === "malca"
    ? "MALCA"
    : (broker?.name || job.brokerName || "ORION");

  const files = [];
  for (const g of guards) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await generate(job, broker, {
      guardNumber: g.guardNumber,
      guardName: g.guardName,
      isDriver: g.isDriver,
      billedSegments: g.billedSegments,
    });
    files.push({
      blob,
      filename: buildFilename(job, brokerLabel, g.guardNumber, guards.length),
      guardNumber: g.guardNumber,
      guardName: g.guardName,
    });
  }
  return { files, kind: choice.kind };
}

/**
 * Trigger a workbook download for this job — one file per assigned guard.
 * @returns {Promise<{ downloaded: boolean, count: number, kind: string, reason?: string }>}
 */
export async function downloadInvoiceWorkbook(job, broker, saveRoot = null) {
  // Each broker files into its own folder — the rate card's name, not the
  // broker's, since they differ ("Malca Amit" vs a folder called "Malca").
  const brokerFolder = broker?.saveFolderName || broker?.name || job?.brokerName;
  // Absent on every existing rate card, so default ON — Malca and the rest
  // keep their year/month folders unless a broker is explicitly opted out.
  const dateFolders = broker?.saveDateFolders !== false;
  const result = await generateInvoiceWorkbooks(job, broker);
  if (result.kind === "none") {
    return { downloaded: false, count: 0, kind: "none", reason: result.reason };
  }
  // With a granted folder the files land in <root>/<year>/<Month year>/.
  // Anything that stops that — no folder, permission gone, a write error —
  // falls back to a normal download so the owner still gets the file.
  const saved = [];
  for (const f of result.files) {
    // eslint-disable-next-line no-await-in-loop
    const path = saveRoot ? await saveFileToFolder(saveRoot, job, f.filename, f.blob, { brokerFolder, dateFolders }) : null;
    if (path) saved.push(path);
    else triggerDownload(f.blob, f.filename);
  }
  return { downloaded: true, count: result.files.length, kind: result.kind, saved };
}
